import {
  OutlineButtonWithArrow,
  SolidButtonWithArrow,
  TrackedMotionButtonProps,
} from "@/components/StoreComponents/StoreButton";
import StoreConfetti, {
  StoreConfettiProps,
} from "@/components/StoreComponents/StoreConfetti";
import Box from "@mui/material/Box";
import { SxProps, styled } from "@mui/material/styles";
import { HTMLAttributes, PropsWithChildren } from "react";
import {
  Link,
  LinkProps as RouterLinkProps,
  useNavigate,
} from "react-router-dom";

type LinkProps = RouterLinkProps | { to: number };

interface StoreFinishProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle: string | React.ReactNode;
  confettiProps?: StoreConfettiProps;
  sx?: SxProps;
  children?: React.ReactNode;
}

interface StoreFinishButtonProps extends TrackedMotionButtonProps {
  sx?: SxProps;
}

interface StoreFinishLinkProps extends StoreFinishButtonProps {
  linkProps: LinkProps;
}

const StoreFinishWrapper = styled("div")(({ theme }) => ({
  maxWidth: 655,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  flex: 1,

  ".store-finish-title": {
    marginBottom: 9,
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.primary.main,
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: 24,
    },
  },
  ".store-finish-subtitle": {
    maxWidth: 694,
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 40,
    color: theme.palette.primary.main,
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
    },
  },
}));

export const StoreFinishButton = ({
  sx,
  children,
  ...props
}: StoreFinishButtonProps) => {
  return (
    <SolidButtonWithArrow
      style={{
        whiteSpace: "nowrap",
        width: "100%",
        paddingTop: 1.75,
        height: 50,
        paddingBottom: 1.75,
        marginBottom: 2,
        fontSize: "16px",
      }}
      {...props}
    >
      {children}
    </SolidButtonWithArrow>
  );
};

export const StoreFinishLink = ({
  children,
  style,
  ...props
}: RouterLinkProps) => {
  return (
    <Link
      {...props}
      style={{
        textDecoration: "none",
        width: "100%",
        display: "flex",
        justifyContent: "center",

        ...style,
      }}
    >
      {children}
    </Link>
  );
};

export const StoreFinishLinkButton = ({
  children,
  sx,
  linkProps,
  ...props
}: StoreFinishLinkProps) => {
  const navigate = useNavigate();
  const { to, ...restLinkProps } = linkProps;
  const btn = (
    <OutlineButtonWithArrow
      style={{
        width: "100%",
        height: 50,
        whiteSpace: "nowrap",
        fontSize: 16,
        paddingTop: 1.75,
        paddingBottom: 1.75,
      }}
      {...props}
      onClick={(e) => {
        props.onClick?.(e);
        if (typeof to === "number") {
          navigate(to);
        }
      }}
    >
      {children}
    </OutlineButtonWithArrow>
  );
  if (typeof to === "number") {
    return <>{btn}</>;
  }
  return (
    <StoreFinishLink to={to} {...restLinkProps}>
      {btn}
    </StoreFinishLink>
  );
};

export const StoreFinishButtonGroup = ({
  children,
  ...props
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <Box
      sx={(theme) => ({
        width: "max-content",
        maxWidth: "100%",
        minWidth: 355,
        [theme.breakpoints.down("sm")]: {
          minWidth: 350,
        },
        [theme.breakpoints.down("xs")]: {
          minWidth: "100%",
        },
      })}
      {...props}
    >
      {children}
    </Box>
  );
};

const ConfettiContainer = styled(Box, {
  label: "ConfettiContainer",
})({
  width: "100vw",
  //   position: "absolute",
  //   top: 0,
  //   left: 0,
  maxWidth: 1000,
  height: "100%",
  overflowX: "visible",
  overflowY: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: -1,
});

export const StoreFinish = ({
  title,
  subtitle,
  children,
  confettiProps: { style: confettiStyle, ...confettiProps } = {},
  ...props
}: StoreFinishProps) => {
  return (
    <StoreFinishWrapper {...props}>
      <ConfettiContainer>
        <StoreConfetti {...confettiProps} />
      </ConfettiContainer>
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          border: "solid 1px #dcdddc",
          borderRadius: "12px",
          minWidth: 300,
          [theme.breakpoints.up("sm")]: {
            minWidth: 400,
          },
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        })}
      >
        <Box
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "primary.main",
          }}
        >
          {title}
        </Box>
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            color: "primary.main",
            paddingBottom: "16px",
          }}
        >
          {subtitle}
        </Box>
        {children}
      </Box>
    </StoreFinishWrapper>
  );
};
